import {
  Combobox,
  type ComboboxRootChangeEventDetails,
} from "@base-ui/react/combobox";
import { Field } from "@base-ui/react/field";
import { cva } from "class-variance-authority";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { usePortalHost } from "../../providers/portal-host";
import { FieldLabel } from "../field-label/field-label";
import { Icon, type IconName } from "../icon";
import {
  MenuItemLayout,
  menuItemClassName,
  menuSurfaceClassName,
} from "../menu/menu-internals";

export interface SelectOption {
  value: string;
  label: string;
  icon?: IconName;
  disabled?: boolean;
}

export interface SelectProps {
  /** ID applied to the interactive trigger. Useful for external labels. */
  id?: string;
  /** Available options. */
  options: SelectOption[];
  /** Controlled selected value. */
  value?: string | null;
  /** Callback fired when the selected value changes. Receives null when selection is cleared. */
  onChange?: (
    value: string | null,
    eventDetails: ComboboxRootChangeEventDetails,
  ) => void;
  /** Default value for uncontrolled usage. */
  defaultValue?: string | null;
  /** Placeholder text shown when no value is selected. */
  placeholder?: string;
  /** Visual size of the select. Defaults to "md". */
  size?: "sm" | "md";
  /** Label text rendered above the trigger. */
  label?: string;
  /** Tooltip content displayed as an info icon in the label row. */
  tooltip?: string;
  /** Shows a required asterisk in the label row. */
  required?: boolean;
  /** Helper text shown below the trigger. Replaced by error when present. */
  description?: string;
  /** Error message. Presence sets error state; takes precedence over description. */
  error?: string;
  /** Disables the select. Trigger dims to 50% opacity; label and subtext remain opaque. */
  disabled?: boolean;
  /** Reserves vertical space for helper or error text. Defaults to true. */
  reserveSubtextSpace?: boolean;
  /** Name attribute for the hidden form input. */
  name?: string;
  /** Test id applied to the interactive trigger. */
  "data-testid"?: string;
  /** Optional custom content for the selected option in the trigger. */
  renderValue?: (option: SelectOption | undefined) => React.ReactNode;
  /** Optional custom content for an option in the popup. */
  renderOption?: (option: SelectOption) => React.ReactNode;
  /** Popup width. Defaults to matching the trigger. */
  popupWidth?: "anchor" | "content";
}

const triggerVariants = cva(
  [
    "flex",
    "items-center",
    "justify-between",
    "gap-(--spacing-4)",
    "w-full",
    "rounded-(--radius-sm)",
    "border",
    "border-solid",
    "border-input-default-outline",
    "bg-input-default-background",
    "text-input-text-default",
    "transition-colors",
    "duration-200",
    "ease-in-out",
    "outline-none",
    "cursor-pointer",
    "data-[disabled]:cursor-not-allowed",
    "[&_svg]:pointer-events-none",
    "[&_svg]:shrink-0",
    // Hover (only when not open)
    "[&:not([data-popup-open])]:hover:bg-input-hover-background",
    "[&:not([data-popup-open])]:hover:border-input-hover-outline",
    // Active/open
    "data-[popup-open]:border-input-active-outline",
    // Pressed
    "active:border-input-pressed-outline",
    // Focus ring (keyboard)
    "focus-visible:ring-2",
    "focus-visible:ring-input-focus-ring",
    "focus-visible:ring-offset-1",
  ],
  {
    variants: {
      size: {
        sm: ["h-(--spacing-32)", "px-(--spacing-10)", "py-(--spacing-6)"],
        md: ["h-(--spacing-40)", "px-(--spacing-10)", "py-(--spacing-10)"],
      },
    },
    defaultVariants: { size: "md" },
  },
);

/**
 * Single-value select field with label, helper text, error state, and icon support.
 *
 * @example
 * <Select
 *   label="Country"
 *   placeholder="Select a country"
 *   options={[{ value: "us", label: "United States" }, { value: "ca", label: "Canada" }]}
 * />
 * <Select label="Priority" options={options} error="Selection is required" />
 */
function Select({
  id,
  options,
  value,
  onChange,
  defaultValue,
  placeholder,
  size = "md",
  label,
  tooltip,
  required,
  description,
  error,
  disabled,
  reserveSubtextSpace = true,
  name,
  "data-testid": dataTestId,
  renderValue,
  renderOption,
  popupWidth = "anchor",
}: SelectProps) {
  const portalHost = usePortalHost();
  const hasError = Boolean(error);
  const hasDescription = Boolean(description);
  const showSubtextRow = reserveSubtextSpace || hasError || hasDescription;
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<string | null>(
    defaultValue ?? null,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const currentValue = isControlled ? value : internalValue;
  const selectedOption = options.find(
    (option) => option.value === currentValue,
  );

  function handleValueChange(
    nextOption: SelectOption | null,
    eventDetails: ComboboxRootChangeEventDetails,
  ) {
    const shouldClearSelection =
      eventDetails.reason === "item-press" &&
      !required &&
      currentValue !== null &&
      nextOption?.value === currentValue;

    const resolvedValue = shouldClearSelection
      ? null
      : (nextOption?.value ?? null);

    onChange?.(resolvedValue, eventDetails);

    if (!isControlled && !eventDetails.isCanceled) {
      setInternalValue(resolvedValue);
    }
  }

  return (
    <Field.Root
      invalid={hasError}
      disabled={disabled}
      className="flex flex-col w-full"
    >
      {label && (
        <FieldLabel label={label} required={required} tooltip={tooltip} />
      )}

      <Combobox.Root<SelectOption>
        value={selectedOption ?? null}
        onValueChange={handleValueChange}
        disabled={disabled}
        required={required}
        name={name}
        modal={false}
        items={options}
        isItemEqualToValue={(option, selected) =>
          option.value === selected.value
        }
        itemToStringLabel={(option) => option.label}
        itemToStringValue={(option) => option.value}
        inputValue={isOpen ? inputValue : (selectedOption?.label ?? "")}
        onInputValueChange={setInputValue}
        onOpenChange={(open) => {
          setIsOpen(open);

          if (open) {
            setInputValue("");
          }
        }}
      >
        <Combobox.InputGroup
          className={cn(
            triggerVariants({ size }),
            "relative",
            disabled && "opacity-50 cursor-not-allowed",
            hasError && [
              "border-input-error-outline",
              "[&:not([data-popup-open])]:hover:border-input-error-outline",
            ],
          )}
        >
          {!isOpen && (
            <span className="pointer-events-none absolute inset-y-0 left-(--spacing-10) right-(--spacing-32) flex min-w-0 items-center text-left text-(length:--font-size-body-medium) leading-(--font-line-height-20)">
              {renderValue ? (
                renderValue(selectedOption)
              ) : selectedOption ? (
                <span className="truncate">{selectedOption.label}</span>
              ) : (
                <span className="truncate text-input-text-placeholder">
                  {placeholder}
                </span>
              )}
            </span>
          )}
          <Combobox.Input
            id={id}
            data-testid={dataTestId}
            aria-label={
              label || id ? undefined : (placeholder ?? "Select option")
            }
            aria-required={required || undefined}
            className={cn(
              "min-w-0 flex-1 bg-transparent outline-none text-left text-(length:--font-size-body-medium) leading-(--font-line-height-20) text-input-text-default placeholder:text-input-text-placeholder",
              !isOpen && "text-transparent caret-transparent",
            )}
            placeholder={isOpen ? "Search" : undefined}
            onFocus={(event) => event.currentTarget.select()}
          />
          <Combobox.Trigger
            aria-label="Open options"
            className="inline-flex shrink-0 text-input-text-default outline-none"
          >
            <Icon name="chevron-down" size="xs" tone="inherit" />
          </Combobox.Trigger>
        </Combobox.InputGroup>

        <Combobox.Portal container={portalHost}>
          <Combobox.Positioner
            sideOffset={4}
            className={cn(
              "z-[var(--z-overlay-popover,180)]",
              popupWidth === "content"
                ? "w-auto min-w-(--anchor-width) max-w-[calc(100vw-var(--spacing-16))]"
                : "w-(--anchor-width)",
            )}
          >
            <Combobox.Popup className={menuSurfaceClassName({ size: "auto" })}>
              <Combobox.List className="flex flex-col outline-none">
                {(option: SelectOption) => (
                  <Combobox.Item
                    key={option.value}
                    value={option}
                    disabled={option.disabled}
                    className={menuItemClassName({})}
                  >
                    {option.icon && (
                      <span className="inline-flex shrink-0 text-menu-icon-default">
                        <Icon name={option.icon} size="xs" tone="inherit" />
                      </span>
                    )}
                    <MenuItemLayout checked={option.value === currentValue}>
                      {renderOption ? renderOption(option) : option.label}
                    </MenuItemLayout>
                  </Combobox.Item>
                )}
              </Combobox.List>
              <Combobox.Empty className="[&:empty]:p-0 px-(--spacing-10) py-(--spacing-8) text-(length:--font-size-body-medium) leading-(--font-line-height-20) text-menu-text-subtle">
                No results available
              </Combobox.Empty>
            </Combobox.Popup>
          </Combobox.Positioner>
        </Combobox.Portal>
      </Combobox.Root>

      {showSubtextRow && (
        <div
          data-testid="select-subtext-row"
          className={cn(
            "mt-(--spacing-4)",
            reserveSubtextSpace && "min-h-(--spacing-20)",
          )}
        >
          {hasError ? (
            <Field.Error
              match
              className="text-(length:--font-size-label-small) leading-(--font-line-height-16) text-input-error-text"
            >
              {error}
            </Field.Error>
          ) : description ? (
            <Field.Description className="text-(length:--font-size-label-small) leading-(--font-line-height-16) text-input-text-supporting">
              {description}
            </Field.Description>
          ) : null}
        </div>
      )}
    </Field.Root>
  );
}

export { Select };
